import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left.mjs'
import KeyRound from 'lucide-react/dist/esm/icons/key-round.mjs'
import Mail from 'lucide-react/dist/esm/icons/mail.mjs'
import Phone from 'lucide-react/dist/esm/icons/phone.mjs'
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check.mjs'
import { FormEvent, useEffect, useState } from 'react'
import { ApiError, api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { InlineNotice } from '../components/InlineNotice'
import { Logo } from '../components/Logo'
import { Link, Navigate, useNavigate } from '../routing/Router'

interface ProviderStatus { id: string; label: string; enabled: boolean; note?: string }

type Method = 'PASSWORD' | 'EMAIL' | 'PHONE'
type View = 'login' | 'forgot' | 'reset'

export function LoginPage() {
  const [method, setMethod] = useState<Method>('PASSWORD')
  const [view, setView] = useState<View>('login')
  const [destination, setDestination] = useState('')
  const [password, setPassword] = useState('')
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [developmentCode, setDevelopmentCode] = useState<string | null>(null)
  const [resetToken, setResetToken] = useState<string | null>(null)
  const [providers, setProviders] = useState<ProviderStatus[]>([])
  const [providersLoaded, setProvidersLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const { user, requestCode, verifyCode, passwordLogin, requestPasswordReset, confirmPasswordReset, demoLogin } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    api.get<ProviderStatus[]>('/api/v1/auth/providers')
      .then(setProviders)
      .catch(() => setProviders([]))
      .finally(() => setProvidersLoaded(true))
    const query = new URLSearchParams(window.location.search)
    const token = query.get('reset')
    if (token) {
      setResetToken(token)
      setView('reset')
      // Токен не должен оставаться в адресной строке и в истории браузера.
      window.history.replaceState(null, '', '/login')
    }
    const oauthError = query.get('oauth_error')
    if (oauthError) setError(oauthError === 'not_configured' ? 'Вход через Яндекс ещё не настроен' : 'Не удалось завершить вход через Яндекс ID')
  }, [])

  if (user) return <Navigate to="/app" replace />

  const provider = (id: string) => providers.find((item) => item.id === id)
  const emailEnabled = provider('email')?.enabled === true
  const phoneEnabled = provider('phone')?.enabled === true
  const resetEnabled = provider('password-reset')?.enabled === true
  const methodEnabled = method === 'PASSWORD' ? true : method === 'EMAIL' ? emailEnabled : phoneEnabled

  function switchMethod(next: Method) {
    setMethod(next)
    setView('login')
    setChallengeId(null)
    setCode('')
    setDevelopmentCode(null)
    setError(null)
    setNotice(null)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      if (view === 'reset') {
        await confirmPasswordReset(resetToken ?? '', password)
        setResetToken(null)
        setView('login')
        setMethod('PASSWORD')
        setPassword('')
        setNotice('Пароль обновлён. Теперь войдите с новым паролем')
      } else if (view === 'forgot') {
        const result = await requestPasswordReset(destination)
        setNotice(result.developmentToken
          ? `Локальный режим: ссылка восстановления /login?reset=${result.developmentToken}`
          : 'Если такой адрес зарегистрирован, письмо со ссылкой уже отправлено. Ссылка действует 30 минут')
      } else if (method === 'PASSWORD') {
        await passwordLogin(destination, password)
        navigate('/app')
      } else if (!challengeId) {
        const result = await requestCode(method, destination)
        setChallengeId(result.challengeId)
        setDevelopmentCode(result.developmentCode ?? null)
        if (result.developmentCode) setCode(result.developmentCode)
      } else {
        await verifyCode(challengeId, code)
        navigate('/app')
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не удалось выполнить вход')
    } finally {
      setBusy(false)
    }
  }

  async function openDemo() {
    setBusy(true)
    setError(null)
    try {
      await demoLogin()
      navigate('/app')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Демо недоступно')
    } finally {
      setBusy(false)
    }
  }

  const heading = view === 'reset' ? 'Новый пароль'
    : view === 'forgot' ? 'Восстановление пароля'
      : challengeId ? 'Введите код' : 'Войти в кабинет'
  const subheading = view === 'reset' ? 'Придумайте пароль не короче 10 символов'
    : view === 'forgot' ? 'Пришлём ссылку для смены пароля на указанный адрес'
      : challengeId ? `Код отправлен: ${destination}`
        : method === 'PASSWORD' ? 'Email и пароль' : 'Одноразовый код без пароля'
  const submitLabel = view === 'reset' ? 'Сохранить пароль'
    : view === 'forgot' ? 'Отправить ссылку'
      : method === 'PASSWORD' ? 'Войти' : challengeId ? 'Войти' : 'Получить код'

  return (
    <main className="login-page">
      <Link className="login-back" to="/"><ArrowLeft size={18} />На главную</Link>
      <section className="login-panel">
        <Logo />
        <div className="login-heading">
          <h1>{heading}</h1>
          <p>{subheading}</p>
        </div>

        {view === 'login' && !challengeId && (
          <div className="login-tabs" role="tablist">
            <button className={method === 'PASSWORD' ? 'active' : ''} onClick={() => switchMethod('PASSWORD')} role="tab" type="button"><KeyRound size={18} />Пароль</button>
            {emailEnabled && <button className={method === 'EMAIL' ? 'active' : ''} onClick={() => switchMethod('EMAIL')} role="tab" type="button"><Mail size={18} />Код на email</button>}
            {phoneEnabled && <button className={method === 'PHONE' ? 'active' : ''} onClick={() => switchMethod('PHONE')} role="tab" type="button"><Phone size={18} />Телефон</button>}
          </div>
        )}

        {providersLoaded && (methodEnabled || view !== 'login') ? <form onSubmit={submit} className="login-form">
          {view === 'reset' ? (
            <label>
              <span>Новый пароль</span>
              <input autoFocus type="password" autoComplete="new-password" minLength={10} value={password} onChange={(event) => setPassword(event.target.value)} required />
            </label>
          ) : challengeId ? (
            <label>
              <span>Одноразовый код</span>
              <input className="otp-input" autoFocus inputMode="numeric" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} required />
            </label>
          ) : (
            <>
              <label>
                <span>{method === 'PHONE' ? 'Номер телефона' : 'Email'}</span>
                <input
                  autoFocus
                  type={method === 'PHONE' ? 'tel' : 'email'}
                  autoComplete={method === 'PHONE' ? 'tel' : 'username'}
                  placeholder={method === 'PHONE' ? '+7 999 000-00-00' : 'name@example.ru'}
                  value={destination}
                  onChange={(event) => setDestination(event.target.value)}
                  required
                />
              </label>
              {view === 'login' && method === 'PASSWORD' && (
                <label>
                  <span>Пароль</span>
                  <input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
                </label>
              )}
            </>
          )}
          {developmentCode && <InlineNotice kind="warning">Локальный режим: код подставлен автоматически.</InlineNotice>}
          {notice && <InlineNotice kind="warning">{notice}</InlineNotice>}
          {error && <InlineNotice kind="error">{error}</InlineNotice>}
          <button className="button button-primary button-wide" disabled={busy}>{busy ? 'Проверяем…' : submitLabel}</button>
          {view === 'login' && method === 'PASSWORD' && resetEnabled && (
            <button type="button" className="text-button" onClick={() => { setView('forgot'); setError(null); setNotice(null) }}>Забыли пароль?</button>
          )}
          {view === 'login' && challengeId && (
            <button type="button" className="text-button" onClick={() => { setChallengeId(null); setCode(''); setDevelopmentCode(null) }}>Изменить адрес</button>
          )}
          {view !== 'login' && (
            <button type="button" className="text-button" onClick={() => { setView('login'); setResetToken(null); setError(null) }}>Вернуться ко входу</button>
          )}
        </form> : providersLoaded && <InlineNotice kind="warning">Этот способ входа пока не подключён.</InlineNotice>}

        <div className="login-separator"><span>или</span></div>
        <div className="identity-buttons">
          <button disabled={!provider('yandex')?.enabled} onClick={() => window.location.assign('/api/v1/auth/oauth/yandex/start')}>Яндекс ID {provider('yandex')?.enabled ? '' : '· скоро'}</button>
          <button disabled={!provider('sber')?.enabled}>Сбер ID · подключение</button>
          <button disabled={!provider('esia')?.enabled}>ЕСИА · подключение</button>
        </div>
        {provider('demo')?.enabled && <button className="demo-button" onClick={openDemo} disabled={busy}>Открыть демо-кабинет</button>}
        <div className="login-safety"><ShieldCheck size={18} /><span>Мы не получаем пароль от почты, банка или Госуслуг</span></div>
      </section>
    </main>
  )
}

export default LoginPage
